/**
 * https://zoopla-eng.github.io/bricks/?path=/docs/foundation-colours-theme--docs
 *
 * Zoopla's storybook is very detailed, and the colour theme docs have 456
 * different semantic tokens. Including them all would have a noticeable size
 * on the bundle, so we've copied them all here, but commented out all the
 * colours we're not actively using.
 *
 * We're also grouping them by prefix, to make them easier to consume, so
 * graphicBackgroundDefault becomes graphicBackground.default.
 */

export type ZooplaColors = {
  badge: {
    level4: {
      background: string;
      border: string;
      text: string;
    };
  };
  brand: {
    primary: string;
    secondary: string;
    tertiary: string;
  };
  button: {
    primary: {
      backgroundDefault: string;
      backgroundHover: string;
      textDefault: string;
    };
    secondary: {
      backgroundHover: string;
      borderDefault: string;
      textDefault: string;
      textHover: string;
    };
    borderDisabled: string;
    textDisabled: string;
  };
  card: {
    defaultBorderDefault: string;
  };
  checkbox: {
    borderDefault: string;
    borderDisabled: string;
    borderError: string;
    borderHover: string;
    checkedBackgroundDefault: string;
    textDefault: string;
    textDisabled: string;
  };
  feedback: {
    informationBackground: string;
    informationGraphic: string;
    neutralBackground: string;
    neutralGraphic: string;
    warningBackground: string;
    warningGraphic: string;
  };
  functional: {
    focus: string;
  };
  graphicBackground: {
    accentDark: string;
    accentLight: string;
    contrast: string;
    default: string;
  };
  input: {
    border: {
      default: string;
      disabled: string;
      error: string;
      hover: string;
    };
    text: {
      default: string;
      disabled: string;
      placeholder: string;
    };
  };
  progress: {
    backgroundDefault: string;
    indicatorBackgroundDefault: string;
  };
  link: {
    textActive: string;
    textDefault: string;
    textDisabled: string;
    textHover: string;
    textVisited: string;
    underlineActive: string;
    underlineDefault: string;
    underlineHover: string;
    underlineVisited: string;
  };
  radioGroup: {
    helpTextDefault: string;
    selectedBackgroundDefault: string;
    radioBackgroundDefault: string;
    radioBorderDefault: string;
    radioBorderError: string;
    radioBorderHover: string;
    textDefault: string;
  };
  text: {
    body: string;
    body2: string;
    contrast: string;
    disabled: string;
    disabledContrast: string;
    title: string;
  };
};

export const zooplaColors: ZooplaColors = {
  // accordionBorderDefault: "#3227443d", // Accordion default border colour
  // accordionBorderHover: "#32274485", // Accordion hover border colour
  // accordionTextDefault: "#322744", // Accordion default text colour
  // badgeAreaGuideBorder: "#0a5f41", // Area guide badge border colour
  // badgeAttributeText: "#322744", // Attribute badge text colour
  // badgeCompletionDateBackground: "#2c8096", // Completion date badge background colour
  // badgeCompletionDateText: "#ffffff", // Completion date badge text colour
  // badgeCountBackground: "#322744", // Count badge background colour
  // badgeCountText: "#ffffff", // Count badge text colour
  // badgeExpiredBackground: "#cc0000", // Expired badge background colour
  // badgeExpiredText: "#ffffff", // Expired badge text colour
  // badgeFeaturedBackground: "#236184", // Featured badge background colour
  // badgeFeaturedText: "#ffffff", // Featured badge text colour
  // badgeGuestAuthorBackground: "#322744", // Guest author badge background colour
  // badgeGuestAuthorText: "#ffffff", // Guest author badge text colour
  // badgeHomeownerBackground: "#fac8f0", // Homeowner badge background colour
  // badgeLandlordBorder: "#ff91d7", // Landlord badge border colour
  badge: {
    level4: {
      background: "#ffffff", // Level 4 badge background colour
      border: "#949494", // Level 4 badge border colour
      text: "#322744", // Level 4 badge text colour
    },
  },
  // badgeLevel1Background: "#322744", // Level 1 badge background colour
  // badgeLevel1Text: "#ffffff", // Level 1 badge text colour
  // badgeLevel2Background: "#ffc800", // Level 2 badge background colour
  // badgeLevel2Text: "#322744", // Level 2 badge text colour
  // badgeLevel3Background: "#ffffff", // Level 3 badge background colour
  // badgeLevel3Border: "#ffc800", // Level 3 badge border colour
  // badgeLevel3Text: "#322744", // Level 3 badge text colour
  // badgeNewHomeBackground: "#158453", // New-home badge background colour
  // badgeNewHomeText: "#ffffff", // New-home badge text colour
  // badgePremiumBackground: "#8046f1", // Premium badge background colour
  // badgePremiumText: "#ffffff", // Premium badge text colour
  // badgeSavedBackground: "#ffffff", // Saved badge background colour
  // badgeSavedText: "#322744", // Saved badge text colour
  // badgeStatusCautionBackground: "#fff0a0", // Caution badge background colour
  // badgeStatusCautionBorder: "#ffc800", // Caution badge border colour
  // badgeStatusCautionText: "#322744", // Caution badge text colour
  // badgeStatusContrastCautionBackground: "#ffaa00", // Caution badge contrast background colour
  // badgeStatusContrastCautionText: "#322744", // Caution badge contrast text colour
  // badgeStatusContrastFailureBackground: "#ff8080", // Failure badge contrast background colour
  // badgeStatusContrastFailureText: "#322744", // Failure badge contrast text colour
  // badgeStatusContrastSuccessBackground: "#8cd7aa", // Success badge contrast background colour
  // badgeStatusContrastSuccessText: "#322744", // Success badge contrast text colour
  // badgeStatusFailureBackground: "#ffd6d6", // Failure badge background colour
  // badgeStatusFailureBorder: "#ff8080", // Failure badge border colour
  // badgeStatusFailureText: "#322744", // Failure badge text colour
  // badgeStatusSuccessBackground: "#cdf0cd", // Success badge background colour
  // badgeStatusSuccessBorder: "#8cd7aa", // Success badge border colour
  // badgeStatusSuccessText: "#322744", // Success badge text colour
  brand: {
    primary: "#8046f1", // Primary brand colour
    // primaryT0: "#a57bf5", // Primary brand T0 colour
    // primaryT1: "#be9ff8", // Primary brand T1 colour
    // primaryT2: "#ceb7f9", // Primary brand T2 colour
    // primaryT3: "#decefb", // Primary brand T3 colour
    // primaryT4: "#f0e9fd", // Primary brand T4 colour
    // primaryT5: "#faf8fe", // Primary brand T5 colour
    secondary: "#322744", // Secondary brand colour
    // secondaryA0: "#322744ad", // Secondary brand A0 colour
    // secondaryA1: "#32274485", // Secondary brand A1 colour
    // secondaryA2: "#32274466", // Secondary brand A2 colour
    // secondaryA3: "#3227443d", // Secondary brand A3 colour
    // secondaryA4: "#3227441f", // Secondary brand A4 colour
    // secondaryA5: "#3227440a", // Secondary brand A5 colour
    tertiary: "#662dbe", // Tertiary brand colour
  },
  // buttonBackgroundDisabled: "#e8e7e6", // Disabled button background colour
  // buttonContrastBackgroundDisabled: "#ffffff0a", // Disabled button contrast background colour
  // buttonContrastBorderDisabled: "#ffffff1f", // Disabled button contrast border colour
  // buttonContrastTextDisabled: "#ffffff1f", // Disabled button contrast text and icon colour
  button: {
    borderDisabled: "#e8e7e6", // Disabled button border colour
    textDisabled: "#d1d0cf", // Disabled button text and icon colour
    primary: {
      backgroundDefault: "#8046f1", // Primary button background default colour
      backgroundHover: "#662dbe", // Primary button background hover colour
      textDefault: "#ffffff", // Primary button text and icon colour
      // buttonPrimaryBackgroundActive: "#662dbe", // Primary button background active colour
      // buttonPrimaryContrastBackgroundActive: "#decefb", // Primary button contrast background active colour
      // buttonPrimaryContrastBackgroundDefault: "#ffffff", // Primary button contrast background default colour
      // buttonPrimaryContrastBackgroundHover: "#decefb", // Primary button contrast background hover colour
      // buttonPrimaryContrastTextDefault: "#322744", // Primary button contrast text and icon colour
    },
    secondary: {
      backgroundHover: "#322744", // Secondary button background hover colour
      borderDefault: "#322744", // Secondary button border colour
      textDefault: "#322744", // Secondary button text and icon default colour
      textHover: "#ffffff", // Secondary button text and icon hover colour
      // buttonSecondaryBackgroundActive: "#322744", // Secondary button background active colour
      // buttonSecondaryBackgroundSelected: "#322744", // Secondary button background selected colour
      // buttonSecondaryContrastBackgroundActive: "#ffffff", // Secondary button contrast background active colour
      // buttonSecondaryContrastBackgroundHover: "#ffffff", // Secondary button contrast background hover colour
      // buttonSecondaryContrastBackgroundSelected: "#ffffff", // Secondary button contrast background selected colour
      // buttonSecondaryContrastBorderDefault: "#ffffff", // Secondary button contrast border colour
      // buttonSecondaryContrastTextActive: "#322744", // Secondary button contrast text and icon active colour
      // buttonSecondaryContrastTextDefault: "#ffffff", // Secondary button contrast text and icon default colour
      // buttonSecondaryContrastTextHover: "#322744", // Secondary button contrast text and icon hover colour
      // buttonSecondaryContrastTextSelected: "#322744", // Secondary button contrast text and icon selected colour
      // buttonSecondaryTextActive: "#ffffff", // Secondary button text and icon active colour
      // buttonSecondaryTextSelected: "#ffffff", // Secondary button text and icon selected colour
    },
  },
  // buttonTertiaryContrastTextActive: "#ffffff", // Tertiary button contrast text and icon active colour
  // buttonTertiaryContrastTextDefault: "#ffffff", // Tertiary button contrast text and icon default colour
  // buttonTertiaryContrastTextHover: "#ffffff", // Tertiary button contrast text and icon hover colour
  // buttonTertiaryContrastUnderlineActive: "#a57bf5", // Tertiary button contrast underline active colour
  // buttonTertiaryContrastUnderlineDefault: "#be9ff8", // Tertiary button contrast underline default colour
  // buttonTertiaryContrastUnderlineHover: "#a57bf5", // Tertiary button contrast underline hover colour
  // buttonTertiaryIconButtonBackgroundActive: "#3227440a", // Tertiary icon button background active colour
  // buttonTertiaryIconButtonBackgroundHover: "#3227440a", // Tertiary icon button background hover colour
  // buttonTertiaryIconButtonContrastBackgroundActive: "#ffffff1f", // Tertiary icon button contrast background active colour
  // buttonTertiaryIconButtonContrastBackgroundHover: "#ffffff1f", // Tertiary icon button contrast background hover colour
  // buttonTertiaryTextActive: "#322744", // Tertiary button text and icon active colour
  // buttonTertiaryTextDefault: "#322744", // Tertiary button text and icon default colour
  // buttonTertiaryTextHover: "#322744", // Tertiary button text and icon hover colour
  // buttonTertiaryUnderlineActive: "#662dbe", // Tertiary button underline active colour
  // buttonTertiaryUnderlineDefault: "#8046f1", // Tertiary button underline default colour
  // buttonTertiaryUnderlineHover: "#662dbe", // Tertiary button underline hover colour
  // calendarAllDayEventBackground: "#faf8fe", // Calendar all-day-event background colour
  // calendarAllDayEventContrastBackground: "#ffffff1f", // Calendar all-day-event contrast background colour
  // calendarAllDayEventContrastText: "#ffffff", // Calendar all-day-event contrast text colour
  // calendarAllDayEventText: "#322744", // Calendar all-day-event text colour
  // calendarAllDayEventUserBackground: "#f0e9fd", // Calendar all-day-event user background colour
  // calendarAllDayEventUserContrastBackground: "#ffffff1f", // Calendar all-day-event user contrast background colour
  card: {
    defaultBorderDefault: "#d1d0cf", // Card default border colour
  },
  // cardDefaultBorderChecked: "#322744", // Card checked border colour
  // cardFeaturedAgentBackgroundDefault: "#ffc800", // Featured agent card default background colour
  // cardFeaturedAgentBorderDefault: "#ffc800", // Featured agent card default border colour
  // cardFeaturedBackgroundDefault: "#d7f6f5", // Featured cards default background colour
  // cardFeaturedBorderDefault: "#6bbeca", // Featured card default border colour
  // cardPremiumBackgroundDefault: "#be9ff8", // Premium card default background colour
  // cardPremiumBorderDefault: "#8046f1", // Premium card default border colour
  // cardPremiumThumbBorderActive: "#be9ff8", // Premium card thumbnail active border colour
  checkbox: {
    borderDefault: "#322744ad", // Checkbox default border colour
    borderDisabled: "#e8e7e6", // Checkbox disabled border colour
    borderError: "#cc0000", // Checkbox error border colour
    borderHover: "#32274485", // Checkbox hover border colour
    checkedBackgroundDefault: "#322744", // Checkbox checked default background colour
    textDefault: "#322744", // Checkbox default label colour
    textDisabled: "#d1d0cf", // Checkbox disabled label colour
  },
  // checkboxBackgroundDefault: "#ffffff", // Checkbox default background colour
  // checkboxBorderActive: "#322744", // Checkbox active border colour
  // checkboxCheckedBackgroundDisabled: "#e8e7e6", // Checkbox checked disabled background colour
  // checkboxCheckedBackgroundError: "#cc0000", // Checkbox checked error background colour
  // checkboxCheckedBackgroundHover: "#322744ad", // Checkbox checked hover background colour
  // checkboxCheckedTickDefault: "#ffffff", // Checkbox checked default tick colour
  // checkboxHelpTextDefault: "#595963", // Checkbox default help-text colour
  // checkboxHelpTextDisabled: "#d1d0cf", // Checkbox disabled help-text colour
  // checkboxHelpTextError: "#cc0000", // Checkbox error help-text colour
  // checkboxIndeterminateBackgroundDefault: "#322744", // Checkbox indeterminate default background colour
  // checkboxIndeterminateBackgroundDisabled: "#e8e7e6", // Checkbox indeterminate disabled background colour
  // checkboxIndeterminateBackgroundError: "#cc0000", // Checkbox indeterminate error background colour
  // checkboxIndeterminateBackgroundHover: "#322744ad", // Checkbox indeterminate hover background colour
  // checkboxIndeterminateIconDefault: "#ffffff", // Checkbox indeterminate default icon colour

  // dataVisBaseBackgroundDefault: "#f7f6f5", // Data-vis base background default colour
  // dataVisBaseGridlinesDefault: "#3227441f", // Data-vis base gridlines default colour
  // dataVisBaseJourneyLine: "#8046f1", // Data-vis base background colour
  // dataVisBaseNoData: "#d1d0cf", // Data-vis base no data colour
  // dataVisBaseTextDefault: "#322744", // Data-vis base text default colour
  // dataVisDivergingAgree: "#8046f1", // Data-vis diverging agree colour
  // dataVisDivergingDisagree: "#ffaa00", // Data-vis diverging disagree colour
  // dataVisDivergingMinus10: "#b13975", // Data-vis diverging minus 10 colour
  // dataVisDivergingMinus15: "#911e55", // Data-vis diverging minus 15 colour
  // dataVisDivergingMinus5: "#d25596", // Data-vis diverging minus 5 colour
  // dataVisDivergingPlus10: "#8046f1", // Data-vis diverging plus 10 colour
  // dataVisDivergingPlus15: "#662dbe", // Data-vis diverging plus 15 colour
  // dataVisDivergingPlus5: "#be9ff8", // Data-vis diverging plus 5 colour
  // dataVisDivergingSlightlyAgree: "#be9ff8", // Data-vis diverging slightly-agree colour
  // dataVisDivergingSlightlyDisagree: "#ffc800", // Data-vis diverging slightly-disagree colour
  // dataVisDivergingStronglyAgree: "#662dbe", // Data-vis diverging strongly-agree colour
  // dataVisDivergingStronglyDisagree: "#f58c00", // Data-vis diverging strongly-disagree colour
  // dataVisDivergingUndecided: "#f0e9fd", // Data-vis diverging undecided colour
  // dataVisDivergingZero: "#f0e9fd", // Data-vis diverging zero colour
  // dataVisQualitativeCategory1: "#8046f1", // Data-vis qualitative category 1 colour
  // dataVisQualitativeCategory2: "#ffc800", // Data-vis qualitative category 2 colour
  // dataVisQualitativeCategory3: "#b13975", // Data-vis qualitative category 3 colour
  // dataVisQualitativeCategory4: "#d25596", // Data-vis qualitative category 4 colour
  // dataVisQualitativeCategory51: "#fac8f0", // Data-vis qualitative category 5.1 colour
  // dataVisQualitativeCategory52: "#4ba073", // Data-vis qualitative category 5.2 colour
  // dataVisSequential100: "#faf8fe", // Data-vis sequential colour
  // dataVisSequential200: "#f0e9fd", // Data-vis sequential colour
  // dataVisSequential300: "#decefb", // Data-vis sequential colour
  // dataVisSequential400: "#ceb7f9", // Data-vis sequential colour
  // dataVisSequential500: "#be9ff8", // Data-vis sequential colour
  // dataVisSequential600: "#a57bf5", // Data-vis sequential colour
  // dataVisSequential700: "#8046f1", // Data-vis sequential colour
  // dataVisSequential800: "#662dbe", // Data-vis sequential colour
  // dataVisSequential900: "#322744", // Data-vis sequential colour
  // datePickerDayBackgroundHover: "#3227441f", // Date-picker day background hover colour
  // datePickerDayBackgroundOn: "#322744", // Date-picker day background on colour
  // datePickerDayBackgroundRange: "#3227440a", // Date-picker day background range colour
  // datePickerDayBorderDefault: "#322744", // Date-picker day border default colour
  // datePickerDayNameTextDefault: "#595963", // Date-picker day-name text default colour
  // datePickerDayNameTextDisabled: "#d1d0cf", // Date-picker day-name text disabled colour
  // datePickerDayTextDefault: "#322744", // Date-picker day text default colour
  // datePickerDayTextDisabled: "#d1d0cf", // Date-picker day text disabled colour
  // datePickerDayTextOn: "#ffffff", // Date-picker day text on colour
  // datePickerDayTextOutsideMonth: "#322744ad", // Date-picker day text outside-month colour
  // feedbackFailureBackground: "#ffd6d6", // Feedback failure background colour
  // feedbackFailureGraphic: "#cc0000", // Feedback failure graphic colour
  feedback: {
    informationBackground: "#e6fafa", // Feedback information background colour
    informationGraphic: "#2c8096", // Feedback information graphic colour
    neutralBackground: "#322744", // Feedback neutral background colour
    neutralGraphic: "#ffffff", // Feedback neutral graphic colour
    warningBackground: "#fff8c8", // Feedback warning background colour
    warningGraphic: "#df6648", // Feedback warning graphic colour
  },
  // feedbackNeutralContrastBackground: "#ffffff", // Feedback neutral contrast background colour
  // feedbackNeutralContrastGraphic: "#322744", // Feedback neutral contrast graphic colour
  // feedbackNoResultsBackground: "#3227440a", // No results banner background colour
  // feedbackPageErrorBackground: "#3227440a", // Page error banner background colour
  // feedbackSuccessBackground: "#cdf0cd", // Feedback success background colour
  // feedbackSuccessGraphic: "#158453", // Feedback success graphic colour
  // feedbackToastBackgroundDefault: "#322744", // Toast default background colour
  // feedbackToastContrastBackgroundDefault: "#faf8fe", // Toast default contrast background colour
  // feedbackToastContrastTextDefault: "#322744", // Toast default contrast text colour
  // feedbackToastTextDefault: "#ffffff", // Toast default text colour
  functional: {
    // caution: "#f58c00", // Caution states colour
    // fadeDown: "linear-gradient(0deg, #ffffff00 0%, #ffffffcc 100%)", // Fade down
    // fadeUp: "linear-gradient(0deg, #ffffffcc 0%, #ffffff00 100%)", // Fade up
    // failure: "#cc0000", // Failure states colour
    // failureContrast: "#ff8080", // Failure states contrast colour
    focus: "#fac8f0", // Focus ring colour
    // focusAlt: "#322744", // Focus ring colour for use when background matches focus
    // gradientOverlayDown: "linear-gradient(0deg, #00000000 0%, #000000cc 100%)", // Gradient overlay down
    // gradientOverlayUp: "linear-gradient(0deg, #000000cc 0%, #00000000 100%)", // Gradient overlay up
    // onScrollShadow:
    //   "linear-gradient(0deg, #3227441f 0%, #32274400 16%, #32274400 100%),linear-gradient(0deg, #32274414 0%, #32274400 100%)", // On-scroll shadow - height 8px
    // prepopulated: "#e6fafa", // Input-field pre-populated background colour
    // scrim: "#0000003d", // Scrim colour
    // skeletonWave:
    //   "linear-gradient(90deg, #ffffff00 0%, #ffffff3d 24%, #ffffff7a 50%, #ffffff3d 76%, #ffffff00 100%);", // Skeleton wave gradient
    // success: "#158453", // Success states colour
    // successContrast: "#8cd7aa", // Success states contrast colour
  },
  // graphicAgentClosest: "#ceb7f9", // Closest agent colour
  // graphicAgentHighestPrice: "#8cd7aa", // Highest-price agent colour
  // graphicAgentQuickest: "#a5ebeb", // Quickest agent colour
  // graphicAgentTopRated: "#ffc800", // Top-rated agent colour
  // graphicAreaGuideFactBorderDefault: "#cdf0cd", // Area guide fact border colour
  // graphicAreaGuideFactIconBackgroundDefault: "#cdf0cd", // Area guide fact icon background colour
  // graphicAreaGuideHeroBackgroundDefault: "#cdf0cd", // Area guide hero background colour
  // graphicAuthorPillBackgroundDefault: "#ffffff66", // Author pill background colour
  graphicBackground: {
    accentDark: "#e8e7e6", // Background dark accent colour
    accentLight: "#f7f6f5", // Background Light accent colour
    contrast: "#322744", // Background contrast colour
    default: "#ffffff", // Background default colour
  },
  // graphicBanner100BackgroundDefault: "#ebfae6", // Banner background colour
  // graphicBanner200BackgroundDefault: "#e6fafa", // Banner background colour
  // graphicBanner300BackgroundDefault: "#fae1f5", // Banner background colour
  // graphicBanner400BackgroundDefault: "#fff8c8", // Banner background colour
  // graphicBanner500BackgroundDefault: "#ffe6dc", // Banner background colour
  // graphicBannerBrandBackgroundDefault: "#f0e9fd", // Banner brand background colour
  // graphicBannerContrastBackgroundDefault: "#ffffff1f", // Banner contrast background colour
  // graphicBuyingDark: "#ff91d7", // Buying dark colour
  // graphicBuyingDefault: "#fac8f0", // Buying default colour
  // graphicBuyingLight: "#fae1f5", // Buying light colour
  // graphicDividerDefault: "#3227443d", // Divider default colour
  // graphicDividerDefaultContrast: "#ffffff85", // Divider default contrast colour
  // graphicDividerSubtle: "#3227441f", // Divider subtle colour
  // graphicDividerSubtleContrast: "#ffffff3d", // Divider subtle contrast colour
  // graphicErrorPageDay: "#fae1f5", // Error page 'day theme' background colour
  // graphicErrorPageNight: "#322744", // Error page 'night theme' background colour
  // graphicIconsContrast: "#ffffff", // Icons contrast colour
  // graphicIconsDefault: "#322744", // Icons default colour
  // graphicImprovingDark: "#fa967d", // Improving dark colour
  // graphicImprovingDefault: "#ffd2b9", // Improving default colour
  // graphicImprovingLight: "#ffe6dc", // Improving light colour
  // graphicPictogram100BackgroundDefault: "#ebfae6", // Pictogram background colour
  // graphicPictogram200BackgroundDefault: "#e6fafa", // Pictogram background colour
  // graphicPictogram300BackgroundDefault: "#fae1f5", // Pictogram background colour
  // graphicPictogram400BackgroundDefault: "#fff8c8", // Pictogram background colour
  // graphicPictogram500BackgroundDefault: "#ffe6dc", // Pictogram background colour
  // graphicPictogram600BackgroundDefault: "#f7f6f5", // Pictogram background colour
  // graphicPictogramBrandBackgroundDefault: "#f0e9fd", // Pictogram brand background colour
  // graphicPictogramContrastBackgroundDefault: "#ffffff1f", // Pictogram contrast background colour
  // graphicPriceDark: "#8cd7aa", // Price dark colour
  // graphicPriceDefault: "#cdf0cd", // Price default colour
  // graphicPriceLight: "#ebfae6", // Price light colour
  // graphicQuoteBlock: "#d1d0cf", // block quote line colour
  // graphicQuotePull: "#8046f1", // pull quote line colour
  // graphicRatingStars: "#ffaa00", // Rating stars colour
  // graphicRentingDark: "#a5ebeb", // Renting dark colour
  // graphicRentingDefault: "#d7f6f5", // Renting default colour
  // graphicRentingLight: "#e6fafa", // Renting light colour
  // graphicResearchArticleHeroBackgroundDefault: "#decefb", // Research article hero background colour
  // graphicSellingDark: "#ffc800", // Selling dark colour
  // graphicSellingDefault: "#fff0a0", // Selling default colour
  // graphicSellingLight: "#fff8c8", // Selling light colour
  // hybridButtonBackgroundActive: "#3227440a", // Hybrid button background active colour
  // hybridButtonBackgroundHover: "#3227441f", // Hybrid button background hover colour
  // hybridButtonTextDefault: "#322744", // Hybrid button text and icon colour
  // hybridButtonTextDisabled: "#d1d0cf", // Hybrid button disabled text and icon colour
  input: {
    border: {
      default: "#322744ad", // Input default border colour
      // inputBorderActive: "#322744", // Input active border colour
      disabled: "#e8e7e6", // Input disabled border colour
      error: "#cc0000", // Input error border colour
      hover: "#32274485", // Input hover border colour
      // inputBorderErrorContrast: "#ff8080", // !DEPRECATED! - use inputContrastBorderError
    },
    text: {
      default: "#322744", // Input default text colour
      disabled: "#d1d0cf", // Input disabled text colour
      placeholder: "#76767c", // Input placeholder text colour
    },
  },
  // inputBackgroundDefault: "#ffffff", // Input default background colour
  // inputContrastBackgroundDisabled: "#ffffff0a", // Input disabled contrast background colour
  // inputContrastBorderDisabled: "#ffffff1f", // Input disabled contrast border colour
  // inputContrastBorderError: "#ff8080", // Input error border contrast colour
  // inputContrastHelpText: "#ffffff", // Input contrast help text colour
  // inputContrastHelpTextDisabled: "#ffffff1f", // Input disabled contrast help text colour
  // inputContrastHelpTextError: "#ff8080", // Input error contrast help text colour
  // inputContrastLabelText: "#ffffff", // Input contrast label text colour
  // inputContrastLabelTextDisabled: "#ffffff1f", // Input disabled contrast label text colour
  // inputContrastTextDisabled: "#ffffff1f", // Input disabled contrast text colour
  // inputHelpTextDefault: "#595963", // Input default help text colour
  // inputHelpTextDisabled: "#d1d0cf", // Input disabled help text colour
  // inputHelpTextError: "#cc0000", // Input error help text colour
  // inputHelpTextErrorContrast: "#ff8080", // !DEPRECATED! - use inputContrastHelpTextError
  // inputLabelTextDefault: "#322744", // Input default label text colour
  // inputLabelTextDisabled: "#d1d0cf", // Input disabled label text colour
  // linkContrastTextDefault: "#ffffff", // Link contrast text and icon default colour
  // linkContrastTextDisabled: "#ffffff3d", // Link contrast text and icon disabled colour
  // linkContrastTextVisited: "#d1d0cf", // Link contrast text and icon visited colour
  // linkContrastUnderlineActive: "#a57bf5", // Link contrast underline active colour
  // linkContrastUnderlineDefault: "#be9ff8", // Link contrast underline default colour
  // linkContrastUnderlineHover: "#a57bf5", // Link contrast underline hover colour
  // linkContrastUnderlineVisited: "#be9ff8", // Link contrast underline visited colour
  link: {
    textActive: "#322744", // Link text and icon active colour
    textDefault: "#322744", // Link text and icon default colour
    textDisabled: "#d1d0cf", // Link text and icon disabled colour
    textHover: "#322744", // Link text and icon hover colour
    textVisited: "#595963", // Link text and icon visited colour
    underlineActive: "#662dbe", // Link underline active colour
    underlineDefault: "#8046f1", // Link underline default colour
    underlineHover: "#662dbe", // Link underline hover colour
    underlineVisited: "#8046f1", // Link underline visited colour
  },
  // navigationLinkBackgroundActive: "#3227440a", // Navigation link background active colour
  // navigationLinkBackgroundDefault: "#ffffff", // Navigation link background colour
  // navigationLinkBackgroundDisabled: "#ffffff", // Navigation link background disabled colour
  // navigationLinkBackgroundHover: "#3227441f", // Navigation link background hover colour
  // navigationLinkTextDefault: "#322744", // Navigation link text and icon colour
  // navigationLinkTextDisabled: "#d1d0cf", // Navigation link disabled text and icon colour
  // pinDefaultBackgroundActive: "#322744", // Default pin active background colour
  // pinDefaultBackgroundDefault: "#ffffff", // Default pin default background colour
  // pinDefaultBorderDefault: "#322744", // Default pin default border colour
  // pinDefaultTextActive: "#ffffff", // Default pin active text colour
  // pinDefaultTextDefault: "#322744", // Default pin default text colour
  // pinFeaturedBackgroundActive: "#2c8096", // Featured pin active background colour
  // pinFeaturedBackgroundDefault: "#d7f6f5", // Featured pin default background colour
  // pinFeaturedBorderDefault: "#6bbeca", // Featured pin default border colour
  // pinFeaturedTextActive: "#ffffff", // Featured pin active text colour
  // pinFeaturedTextDefault: "#322744", // Featured pin default text colour
  // pinHomeBackgroundActive: "#ffffff", // Home pin active background colour
  // pinHomeBackgroundDefault: "#322744", // Home pin default background colour
  // pinHomeBorderActive: "#322744", // Home pin active border colour
  // pinJustAddedBackgroundActive: "#ffc800", // Just added pin active background colour
  // pinJustAddedBackgroundDefault: "#fff0a0", // Just added pin default background colour
  // pinJustAddedBorderDefault: "#ffc800", // Just added pin default border colour
  // pinJustAddedTextDefault: "#322744", // Just added pin text colour
  // pinPremiumBackgroundActive: "#662dbe", // Premium pin active background colour
  // pinPremiumBackgroundDefault: "#8046f1", // Premium pin default background colour
  // pinPremiumTextDefault: "#ffffff", // Premium pin text colour
  // pinSoldBackgroundActive: "#ed785a", // Sold pin active background colour
  // pinSoldBackgroundDefault: "#ffd2b9", // Sold pin default background colour
  // pinSoldBorderDefault: "#ed785a", // Sold pin default border colour
  // pinSoldTextActive: "#322744", // Sold pin active text colour
  // pinSoldTextDefault: "#322744", // Sold pin default text colour
  progress: {
    // backgroundCaution: "#fff0a0", // Progress caution background colour
    backgroundDefault: "#decefb", // Progress default background colour
    // backgroundWarning: "#ffd6d6", // Progress warning background colour
    // indicatorBackgroundCaution: "#ffaa00", // Progress indicator caution background colour
    indicatorBackgroundDefault: "#8046f1", // Progress indicator default background colour
    // indicatorBackgroundWarning: "#cc0000", // Progress indicator warning background colour
  },
  radioGroup: {
    helpTextDefault: "#595963", // Radio group default help-text colour
    selectedBackgroundDefault: "#322744", // Radio default selected background colour
    radioBackgroundDefault: "#ffffff", // Radio default background colour
    radioBorderDefault: "#322744ad", // Radio default border colour
    radioBorderError: "#cc0000", // Radio error border colour
    radioBorderHover: "#32274485", // Radio hover border colour
    textDefault: "#322744", // Radio group default text colour
  },
  // radioGroupHelpTextDisabled: "#d1d0cf", // Radio group disabled help-text colour
  // radioGroupHelpTextError: "#cc0000", // Radio group error help-text colour
  // radioGroupRadioBorderActive: "#322744", // Radio active border colour
  // radioGroupRadioBorderDisabled: "#e8e7e6", // Radio disabled border colour
  // radioGroupSelectedBackgroundDisabled: "#e8e7e6", // Radio disabled selected background colour
  // radioGroupSelectedBackgroundError: "#cc0000", // Radio error selected background colour
  // radioGroupTextDisabled: "#d1d0cf", // Radio group disabled text colour
  // sliderBackground: "#3227441f", // Slider track default background colour
  // sliderFill: "#322744", // Slider track default fill colour
  // sliderThumbBackgroundDefault: "#ffffff", // Slider thumb default background colour
  // sliderThumbBackgroundHover: "#322744", // Slider thumb hover background colour
  // sliderThumbBorderDefault: "#322744", // Slider thumb default border colour
  // switchBackgroundDefault: "#32274466", // Switch default background colour
  // switchBackgroundDisabled: "#e8e7e6", // Switch disabled background colour
  // switchBackgroundError: "#cc0000", // Switch error background colour
  // switchBackgroundOn: "#322744", // Switch on background colour
  // switchBorderActive: "#322744", // Switch active border colour
  // switchBorderDefault: "#322744ad", // Switch default border colour
  // switchBorderDisabled: "#e8e7e6", // Switch disabled border colour
  // switchBorderError: "#cc0000", // Switch error border colour
  // switchBorderHover: "#32274485", // Switch hover border colour
  // switchBorderOn: "#322744", // Switch on border colour
  // switchHelpTextDefault: "#595963", // Switch default help-text colour
  // switchHelpTextDisabled: "#d1d0cf", // Switch disabled help-text colour
  // switchHelpTextError: "#cc0000", // Switch error help-text colour
  // switchTextDefault: "#322744", // Switch text colour
  // switchTextDisabled: "#d1d0cf", // Switch disabled text colour
  // switchThumbBackgroundDefault: "#ffffff", // Switch thumb background colour
  // tabBackgroundHover: "#3227440a", // Tab hover background colour
  // tabBorderActive: "#8046f1", // Tab active border colour
  // tabBorderDefault: "#3227443d", // Tab default border colour
  // tabBorderHover: "#662dbe", // Tab hover border colour
  // tableBorderDefault: "#3227443d", // Table border default colour
  // tableCellBackgroundActive: "#faf8fe", // !DEPRECATED!
  // tableCellBackgroundDefault: "#ffffff", // !DEPRECATED!
  // tableCellBackgroundDisabled: "#e8e7e6", // !DEPRECATED!
  // tableCellBackgroundHighlight: "#faf8fe", // !DEPRECATED!
  // tableContrastBorderDefault: "#ffffff66", // Table border contrast default colour
  // tableContrastTbodyBackgroundChecked: "#ffffff1f", // Table body contrast background checked colour
  // tableContrastTbodyBackgroundHighlight: "#ffffff1f", // Table body contrast background highlight colour
  // tableContrastTbodyBorderDefault: "#ffffff66", // Table body contrast border default colour
  // tableContrastTbodyBorderHover: "#ffffff66", // Table body contrast border hover colour
  // tableContrastTbodyBorderLeftChecked: "#ffffff", // Table body contrast left border checked colour
  // tableContrastTheadBackgroundDefault: "#ffffff3d", // Table head contrast background default colour
  // tableContrastTheadBorderDefault: "#ffffff66", // Table head contrast border default colour
  // tableContrastTheadTextDefault: "#ffffff", // Table head contrast text default colour
  // tableHeaderBackgroundContrast: "#f7f6f5", // !DEPRECATED!
  // tableHeaderBackgroundDefault: "#322744", // !DEPRECATED!
  // tableTbodyBackgroundChecked: "#3227440a", // Table body background checked colour
  // tableTbodyBackgroundHighlight: "#3227440a", // Table body background highlight colour
  // tableTbodyBorderDefault: "#3227443d", // Table body border default colour
  // tableTbodyBorderHover: "#3227443d", // Table body border hover colour
  // tableTbodyBorderLeftChecked: "#322744", // Table body left border checked colour
  // tableTheadBackgroundDefault: "#3227441f", // Table head background default colour
  // tableTheadBorderDefault: "#3227443d", // Table head border default colour
  // tableTheadTextDefault: "#322744", // Table head text default colour
  // tabTextDefault: "#322744", // Tab default text colour
  // tagBackgroundActive: "#decefb", // tag active background colour
  // tagBackgroundDefault: "#faf8fe", // tag default background colour
  // tagBackgroundDisabled: "#f7f6f5", // tag disabled background colour
  // tagBackgroundHover: "#f0e9fd", // tag hover background colour
  // tagBorderDefault: "#decefb", // tag default border colour
  // tagBorderDisabled: "#e8e7e6", // tag disabled border colour
  // tagTextDefault: "#322744", // tag default text colour
  // tagTextDisabled: "#d1d0cf", // tag disabled text colour
  text: {
    body: "#322744", // Body text colour
    body2: "#595963", // Supporting body text colour
    contrast: "#ffffff", // Contrast text colour
    disabled: "#d1d0cf", // Disabled text colour
    disabledContrast: "#ffffff1f", // Disabled contrast text colour
    title: "#322744", // Titles text colour
  },
  // toggleBackgroundHover: "#3227441f", // Toggle background hover colour
  // toggleBackgroundOn: "#322744", // Toggle background on colour
  // toggleBorderDefault: "#322744", // Toggle border default colour
  // toggleBorderDisabled: "#e8e7e6", // Toggle border disabled colour
  // toggleContrastBackgroundHover: "#ffffff1f", // Toggle contrast background hover colour
  // toggleContrastBackgroundOn: "#ffffff", // Toggle contrast background on colour
  // toggleContrastBorderDefault: "#ffffff", // Toggle contrast border default colour
  // toggleContrastBorderDisabled: "#ffffff3d", // Toggle contrast border disabled colour
  // toggleContrastTextDefault: "#ffffff", // Toggle contrast text default colour
  // toggleContrastTextDisabled: "#ffffff3d", // Toggle contrast text disabled colour
  // toggleContrastTextOn: "#322744", // Toggle contrast text on colour
  // toggleGroupBorderDefault: "#322744ad", // Toggle-group border default colour
  // toggleGroupContrastBorderDefault: "#ffffffad", // Toggle-group contrast border default colour
  // toggleTextDefault: "#322744", // Toggle text default colour
  // toggleTextDisabled: "#d1d0cf", // Toggle text disabled colour
  // toggleTextOn: "#ffffff", // Toggle text on colour
  // tooltipBackgroundContrast: "#ffffff", // Tooltip contrast background colour
  // tooltipBackgroundDefault: "#322744", // Tooltip default background colour
  // tooltipTextContrast: "#322744", // Tooltip contrast text colour
  // tooltipTextDefault: "#ffffff", // Tooltip default text colour
};
